import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ArrowRight, Trash2, Calendar, User, Package, DollarSign, Edit, Printer } from 'lucide-react';
import { getDealClosures, deleteDealClosure, getCustomers } from '../api';
import { buildNumberLookup } from '../lib/customerSearch';
import { printDocument } from '../lib/printDocument';
import { toast } from '../hooks/use-toast';

const DealClosuresList = () => {
  const navigate = useNavigate();
  const [dealClosures, setDealClosures] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const dealTotal = (deal) => parseFloat(deal.totalAgreedWithCustomer) || (deal.items || []).reduce((s, i) => s + (parseFloat(i.total) || 0), 0);
  const [numberOf, setNumberOf] = useState(() => () => undefined);

  useEffect(() => {
    loadDealClosures();
  }, []);

  const loadDealClosures = async () => {
    try {
      const [deals, customers] = await Promise.all([getDealClosures(), getCustomers().catch(() => [])]);
      const lookup = buildNumberLookup(customers);
      setNumberOf(() => lookup);
      setDealClosures(deals);
    } catch (error) {
      toast({
        title: 'שגיאה',
        description: 'לא ניתן לטעון את העסקאות',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק עסקה זו?')) {
      try {
        await deleteDealClosure(id);
        setDealClosures(dealClosures.filter(deal => deal.id !== id));
        toast({
          title: 'הצלחה',
          description: 'העסקה נמחקה בהצלחה'
        });
      } catch (error) {
        toast({
          title: 'שגיאה',
          description: 'לא ניתן למחוק את העסקה',
          variant: 'destructive'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return amount ? `₪${parseFloat(amount).toFixed(2)}` : '-';
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center" dir="rtl">
        <div className="text-xl text-gray-600">טוען...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 py-12 px-4" dir="rtl">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-8 flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">סגירות עסקאות</h1>
            <p className="text-gray-600">סך הכל {dealClosures.length} עסקאות</p>
          </div>
          <Button
            onClick={() => navigate('/reports')}
            variant="outline"
            className="border-2 hover:bg-gray-50 transition-colors"
          >
            <ArrowRight className="ml-2 w-5 h-5" />
            חזור לדווח
          </Button>
        </div>

        {/* Deal Closures List */}
        {dealClosures.length === 0 ? (
          <Card className="shadow-lg">
            <CardContent className="py-16 text-center">
              <p className="text-xl text-gray-500 mb-6">אין עסקאות שנסגרו עדיין</p>
              <Button
                onClick={() => navigate('/deal-closure')}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white"
              >
                צור סגירת עסקה ראשונה
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {dealClosures.map((deal) => (
              <Card key={deal.id} className="shadow-lg hover:shadow-xl transition-shadow border-0">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2 flex-1">
                      <div className="flex items-center gap-3">
                        <User className="w-5 h-5 text-green-600" />
                        <CardTitle className="text-2xl">{deal.customerName}</CardTitle>
                        {numberOf(deal.customerName) && <span className="font-mono text-sm font-bold bg-white border rounded px-2 py-0.5 text-gray-600" data-testid={`deal-customer-number-${deal.id}`}>מס' לקוח {numberOf(deal.customerName)}</span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-3">
                      <Badge className="bg-green-100 text-green-800 hover:bg-green-200 flex items-center gap-2 px-3 py-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(deal.date)}
                      </Badge>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => printDocument({
                            title: 'סגירת עסקה',
                            customerName: deal.customerName,
                            customerNumber: numberOf(deal.customerName),
                            date: deal.date,
                            items: (deal.items || []).map(i => ({ name: i.partName, quantity: i.quantity, price: i.sellingPrice || ((parseFloat(i.total) || 0) / (parseFloat(i.quantity) || 1)) })),
                            payments: (deal.payments || []).filter(p => p.method && p.amount),
                            total: dealTotal(deal),
                            vatRate: deal.vatRate,
                            docRef: { type: 'deal', recordId: deal.id }
                          })}
                          className="text-gray-700 hover:bg-gray-100"
                          title="הדפסה"
                          data-testid={`print-deal-${deal.id}`}
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/deal-closure?id=${deal.id}`)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(deal.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {/* Items */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-3">
                        <Package className="w-5 h-5 text-gray-600" />
                        <span className="text-sm font-semibold text-gray-700">פריטים ({(deal.items || []).length}):</span>
                      </div>
                      {(deal.items || []).length === 0 ? (
                        <p className="text-gray-400 text-sm">אין פריטים</p>
                      ) : (
                        <div className="space-y-1.5">
                          {(deal.items || []).map((it, idx) => (
                            <div key={idx} className="flex items-center justify-between text-sm border-b border-gray-200 last:border-0 pb-1.5 last:pb-0">
                              <span className="text-gray-900 font-medium">
                                {it.partName || 'ללא שם'}
                                {it.quantity && Number(it.quantity) !== 1 ? <span className="text-gray-500"> × {it.quantity}</span> : null}
                              </span>
                              <span className="text-gray-700">{formatCurrency(it.total)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Deal Price */}
                    <div className="flex items-center gap-3 bg-green-50 p-4 rounded-lg border-r-4 border-green-500">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <div>
                        <span className="text-sm font-semibold text-gray-700">סה&quot;כ סוכם עם הלקוח: </span>
                        <span className="text-2xl font-bold text-green-900">{formatCurrency(dealTotal(deal) * (1 + (parseFloat(deal.vatRate) || 0) / 100))}{parseFloat(deal.vatRate) > 0 && <span className="text-sm font-normal text-gray-500 mr-2">(כולל מע"מ {deal.vatRate}%)</span>}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealClosuresList;